import React, { useState, useEffect, useCallback } from 'react';
import { downloadPdf } from './utils/downloadPdf';
import '../../styles/MonthlyEvaluationForm.css';

const API_URL = 'https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport';

const MonthlyEvaluationForm = ({
  loginCode,
  userName,
  recipientNumber = '',
  onSaved,
  date,
  onDateChange
}) => {
  const [formDate, setFormDate] = useState(() => {
    const base = date ? new Date(date) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1).toISOString().split('T')[0];
  });

  const [form, setForm] = useState({
    startTime: '',
    endTime: '',
    method: '通所',
    methodNote: '',
    goal: '',
    effort: '',
    achievement: '',
    issues: '',
    improvement: '',
    health: '',
    others: '',
    appropriateness: '',
    evaluator: '',
    prevEvalDate: '',
    userName: userName || '',
    recipientNumber: recipientNumber || ''
  });

  const [generating, setGenerating] = useState(false);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const fetchMonthlyEvaluation = useCallback(async () => {
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-monthly-evaluation',
          login_code: loginCode,
          date: formDate
        })
      });
      if (!res.ok) throw new Error('取得失敗');
      const record = await res.json();

      if (record) {
        setForm({
          startTime: record.mark_start?.slice(11, 16) || '',
          endTime: record.mark_end?.slice(11, 16) || '',
          method: record.evaluation_method || '通所',
          methodNote: record.method_other || '',
          goal: record.goal || '',
          effort: record.effort || '',
          achievement: record.achievement || '',
          issues: record.issues || '',
          improvement: record.improvement || '',
          health: record.health || '',
          others: record.others || '',
          appropriateness: record.appropriateness || '',
          evaluator: record.evaluator_name || '',
          prevEvalDate: record.prev_evaluation_date?.slice(0, 10) || '',
          userName: record.user_name || '',
          recipientNumber: record.recipient_number || ''
        });
      }
    } catch (err) {
      console.error('月報取得エラー:', err);
      alert('月報の取得に失敗しました');
    }
  }, [formDate, loginCode]);

  // 🔄 親の date が変わったら同期
  useEffect(() => {
    if (date) {
      const d = new Date(date);
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
      setFormDate(monthStart);
    }
  }, [date]);

  useEffect(() => {
    if (formDate && loginCode) fetchMonthlyEvaluation();
  }, [formDate, loginCode, fetchMonthlyEvaluation]);

  const handleMonthChange = (e) => {
    const newDateStr = `${e.target.value}-01`;
    setFormDate(newDateStr);
    if (onDateChange) {
      onDateChange(new Date(newDateStr)); // ← 親へ連動通知
    }
  };

  const generateMonthly = async () => {
    try {
      setGenerating(true);
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-monthly-evaluation',
          login_code: loginCode,
          date: formDate
        })
      });
      const data = await res.json();
      setForm((prev) => ({ ...prev, ...data }));
    } catch (err) {
      alert('生成に失敗しました');
      console.error('generate-monthly-evaluation', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    const { startTime, endTime, method, methodNote, goal, effort, achievement, issues, improvement, health, others, appropriateness, evaluator, prevEvalDate } = form;
    if (!formDate || !evaluator || !goal || !achievement) {
      alert('実施日・評価実施者・訓練目標・達成度は必須です。');
      return;
    }

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save-monthly-evaluation',
          login_code: loginCode,
          date: formDate,
          mark_start: startTime ? `${formDate}T${startTime}:00` : null,
          mark_end: endTime ? `${formDate}T${endTime}:00` : null,
          evaluation_method: method,
          method_other: method === 'その他' ? methodNote : '',
          goal, effort, achievement, issues, improvement, health, others, appropriateness,
          evaluator_name: evaluator,
          prev_evaluation_date: prevEvalDate || null
        })
      });
      if (!res.ok) throw new Error();
      alert('月報を保存しました');
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert('保存に失敗しました');
    }
  };

  const handleDownloadPdf = () => {
    downloadPdf({
      apiUrl: API_URL,
      action: 'get-monthly-evaluation-pdf',
      loginCode,
      date: formDate,
      fallbackFilename: 'Monthly-Evaluation.pdf'
    });
  };

  return (
    <div className="monthly-evaluation-form">
      <h3 className="form-title">在宅における就労達成度評価シート</h3>

      <div className="form-row">
        <div className="input-block">
          <label className="form-label">対象者</label>
          <input type="text" className="form-input" value={form.userName} onChange={handleChange('userName')} />
        </div>
        <div className="input-block">
          <label className="form-label">受給者証番号</label>
          <input type="text" className="form-input" value={form.recipientNumber} onChange={handleChange('recipientNumber')} />
        </div>
      </div>

      <div className="form-row">
        <div className="input-block">
          <label className="form-label">評価月</label>
          <input
            type="month"
            className="form-input"
            value={formDate.slice(0, 7)}
            onChange={handleMonthChange}
          />
        </div>
        <div className="input-block">
          <label className="form-label">前回の達成度評価日</label>
          <input type="date" className="form-input" value={form.prevEvalDate} onChange={handleChange('prevEvalDate')} />
        </div>
      </div>

      <div className="form-row time-range">
        <div className="input-block">
          <label className="form-label">実施時間（開始）</label>
          <input type="time" className="form-input" value={form.startTime} onChange={handleChange('startTime')} />
        </div>
        <div className="input-block">
          <label className="form-label">実施時間（終了）</label>
          <input type="time" className="form-input" value={form.endTime} onChange={handleChange('endTime')} />
        </div>
      </div>

      <div className="form-row">
        <div className="input-block">
          <label className="form-label">実施方法</label>
          <div className="radio-group">
            {['通所', '訪問', 'その他'].map(opt => (
              <label key={opt} className="radio-option">
                <input type="radio" value={opt} checked={form.method === opt} onChange={handleChange('method')} /> {opt}
              </label>
            ))}
          </div>
        </div>
        {form.method === 'その他' && (
          <div className="input-block">
            <label className="form-label">その他の方法（記述）</label>
            <input type="text" className="form-input" value={form.methodNote} onChange={handleChange('methodNote')} />
          </div>
        )}
      </div>

      <div>
        <button className="custom-button" onClick={generateMonthly} disabled={generating}>
          {generating ? '生成中...' : 'AIで自動生成'}
        </button>
      </div>

      {[['訓練目標', 'goal'], ['取組内容', 'effort'], ['訓練目標に対する達成度', 'achievement'],
        ['課題', 'issues'], ['今後における課題の改善方針', 'improvement'],
        ['健康・体調面での留意事項', 'health'], ['その他特記事項', 'others'],
        ['在宅就労継続の妥当性', 'appropriateness']].map(([label, key]) => (
        <div className="input-block" key={key}>
          <label className="form-label">{label}</label>
          <textarea className="form-textarea" rows={4} value={form[key]} onChange={handleChange(key)} />
        </div>
      ))}

      <div className="input-block">
        <label className="form-label">評価実施者</label>
        <input type="text" className="form-input" value={form.evaluator} onChange={handleChange('evaluator')} />
      </div>

      <div className="save-monthly-report">
        <button className="custom-button right-margin" onClick={handleSave}>保存</button>
        <button
          className="custom-button"
          onClick={() => downloadPdf({
            apiUrl: API_URL,
            action: 'get-monthly-evaluation-pdf',
            loginCode,
            date: formDate,
            fallbackFilename: 'Monthly-Evaluation.pdf'
          })}
        >
          PDFダウンロード
        </button>
      </div>
    </div>
  );
};

export default MonthlyEvaluationForm;
